import type { AxisSpec } from "../ChartSpec";

/** Which names the legend spells out per series. All of them off means no legend at all. */
export type LegendParts = {
    categories: boolean;
    devices: boolean;
    sensorClusters: boolean;
    sensorElements: boolean;
};

/** Vertical room one value tag needs. Also the minimum gap when they are pushed apart. */
export const TAG_MIN_GAP_PX = 15;

export type PanelChrome = {
    /** Badge names the metric when every series is in one unit category. */
    metricCategory: string | null;
    /** Badge names the device when every series comes from one. */
    deviceName: string | null;
    legend: LegendParts;
    hoverTags: boolean;
    /** Tooltip box. Never on together with the tags — it would repeat them and cover the plot. */
    tooltip: boolean;
};

/** The names a series can be told apart by. */
type SeriesNames = {
    name: string;
    unitCategory: string;
    sensorClusterName: string | null;
    sensorElementName: string | null;
};

export function legendShowsAnything(parts: LegendParts): boolean {
    return parts.categories || parts.devices || parts.sensorClusters || parts.sensorElements;
}

/** True when the series do not all share one value. */
function varies(values: ReadonlyArray<string | null>): boolean {
    return values.length > 0 && values.some((value) => value !== values[0]);
}

/** True when no two legend rows would read the same with these parts on. */
function tellsSeriesApart(series: ReadonlyArray<SeriesNames>, parts: LegendParts): boolean {
    const rows = series.map((s) =>
        [
            parts.categories ? s.unitCategory : "",
            parts.devices ? s.name : "",
            parts.sensorClusters ? s.sensorClusterName : "",
            parts.sensorElements ? s.sensorElementName : "",
        ].join("|"),
    );
    return new Set(rows).size === rows.length;
}

/**
 * Legend lists what varies between series; the badge names the metric and the device they share.
 *
 * The metric and the device always go in when they vary. The sensor and channel names are only
 * tiebreakers — a zone panel whose series already differ by metric doesn't need "HS 6" 1" on every
 * row — so each goes in only while two rows would still read the same without it, and neither ever
 * goes on the badge.
 */
export function planPanelChrome({
    axes,
    series,
    plotBottomPx,
}: {
    axes: AxisSpec[];
    series: ReadonlyArray<SeriesNames>;
    plotBottomPx: number;
}): PanelChrome {
    // A bare number means nothing next to one in a different unit.
    const oneCategory = new Set(axes.map((axis) => axis.unitCategory)).size === 1;
    const legend: LegendParts = {
        categories: !oneCategory,
        devices: varies(series.map((s) => s.name)),
        sensorClusters: false,
        sensorElements: false,
    };

    if (!tellsSeriesApart(series, legend)) {
        legend.sensorClusters = varies(series.map((s) => s.sensorClusterName));
    }
    if (!tellsSeriesApart(series, legend)) {
        legend.sensorElements = varies(series.map((s) => s.sensorElementName));
    }

    // Tags fill two columns, one either side of the dot, but edge clipping can push them all onto
    // one side (sideFor falls back to whichever side fits). Series count is the upper bound for
    // that single-column case, so fitting that many means they always fit.
    const hoverTags = oneCategory && series.length * TAG_MIN_GAP_PX <= plotBottomPx;

    return {
        metricCategory: oneCategory ? (axes[0]?.unitCategory ?? null) : null,
        deviceName: legend.devices ? null : (series[0]?.name ?? null),
        legend,
        hoverTags,
        tooltip: !hoverTags,
    };
}
